import React, { createContext, useContext, useRef } from 'react'
import { useTooltip, UseTooltipOptions } from '../Tooltip/useTooltip'
import { CellContainer } from './style'

interface TableTooltipContextType {
  handleMouseEnter: (e: React.MouseEvent<any>, text: string) => void
  handleMouseLeave: () => void
  handleCellClick: (e: React.MouseEvent<any>, text: string) => void
}

const TableTooltipContext = createContext<TableTooltipContextType | null>(null)

interface TableTooltipProviderProps {
  children: React.ReactNode
  options?: UseTooltipOptions
}

export const TableTooltipProvider: React.FC<TableTooltipProviderProps> = ({
  children,
  options = { maxWidth: 500, maxHeight: 280, rowHeight: 22, padding: 16, verticalGap: 4 },
}) => {
  const { tooltipElement, handleMouseEnter, handleMouseLeave, handleCellClick } = useTooltip(options)

  return (
    <TableTooltipContext.Provider value={{ handleMouseEnter, handleMouseLeave, handleCellClick }}>
      {children}
      {tooltipElement}
    </TableTooltipContext.Provider>
  )
}

export const useTableTooltip = () => {
  const context = useContext(TableTooltipContext)
  if (!context) {
    throw new Error('useTableTooltip must be used within a TableTooltipProvider')
  }
  return context
}

interface OverflowCellProps {
  text: string
  children?: React.ReactNode
  forceTooltip?: boolean
  maxWidth?: number
}

export const OverflowCell: React.FC<OverflowCellProps> = ({
  text,
  children,
  forceTooltip = false,
  maxWidth,
}) => {
  const cellRef = useRef<HTMLDivElement>(null)
  const { handleMouseEnter, handleMouseLeave, handleCellClick } = useTableTooltip()

  const onEnter = (e: React.MouseEvent<any>) => {
    const el = cellRef.current
    if (el) {
      const isOverflowing = el.scrollWidth > el.clientWidth || forceTooltip
      if (isOverflowing) {
        handleMouseEnter(e, text)
      }
    }
  }

  const onClick = (e: React.MouseEvent<any>) => {
    const el = cellRef.current
    if (el) {
      const isOverflowing = el.scrollWidth > el.clientWidth || forceTooltip
      if (isOverflowing) {
        handleCellClick(e, text)
      }
    }
  }

  return (
    <CellContainer
      ref={cellRef}
      $maxWidth={maxWidth}
      onMouseEnter={onEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      {children || text}
    </CellContainer>
  )
}

export { CellContainer }
export default OverflowCell
